import { useContext, useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { RetirementProvisionStatus, Status } from "../../assets/data/Constants";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import TkDateDialog from "../../components/molecules/TkDateDialog";
import { changeAmountFormat } from "../../utils/useTools";
import { TkGetCode } from "../../utils/TkGetCode";
import { httpAPI_RET001, httpAPI_RET007 } from "./api/request";
import { fakeTableList, schema } from "./parts";
import _ from "lodash";

/**
 * SCRR410030 退職手当金の請求手続き（振込状況の確認）
 * @return SCRR410030
 */
const SCRR410030 = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);

  const isUserTypeKiko =
    state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO;
  const contractorNo = isUserTypeKiko
    ? businessState.common?.contractorNo
    : state.authInfo?.contractorNo;
  const staffNo = location.state?.staffNo;

  const [stateTableList, setStateTableList] = useState<any>([]);
  const [disable, setDisable] = useState(false);
  const [applyId, setApplyId] = useState(0);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openDate, setOpenDate] = useState(false);
  const [reason, setReason] = useState("");
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));

  // 初期表示
  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let staffName = resArr.find((item) => item.keyword === "staffName")!;

    // 退職者名
    staffName.contractorNumber =
      (location.state?.staffNo ?? "") + ":" + (location.state?.staffName ?? "");
    staffName.showLine = isUserTypeKiko;

    setStateTableList([...resArr]);

    handleCallApiInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCallApiInit = async () => {
    // 退職金支払参照API
    try {
      const res = await httpAPI_RET001({
        contractorNo: contractorNo,
        staffNo: staffNo,
      });

      if (res.resultCd === "I0000") {
        let resArr = stateFakeTableList.current;

        // 請求書の提出完了日
        resArr.find(
          (item) => item.keyword === "retirementInvoiceReceptionDay"
        )!.text = res.retirementInvoiceReceptionDay;

        // 退職手当金の請求に依る処理状況
        resArr.find((item) => item.keyword === "paymentStatus")!.text =
          TkGetCode(RetirementProvisionStatus, res.paymentStatus);

        // 退職手当金の支給予定額（税引前）
        resArr.find((item) => item.keyword === "paymentAmt")!.text =
          res.retirementAllowanceAmt
            ? changeAmountFormat(res.retirementAllowanceAmt ?? 0) + "円"
            : "";

        // 退職手当金の振込（予定）日
        resArr.find((item) => item.keyword === "payDay")!.text = res.payDay;

        // 被共済職員期間
        resArr.find((item) => item.keyword === "maePeriod")!.text =
          (res.maePeriodYear ?? "") +
          "年" +
          (res.maePeriodMonth ?? "") +
          "ヶ月";

        let status: any = [
          Status["届出待ち"],
          Status["受理待ち"],
          Status["差戻し一時保存"],
          Status["添付待ち"],
        ];

        // 取下げ理由
        resArr.find(
          (item) => item.keyword === "cancelReason"
        )!.setDefaultValue = res.cancelReason;
        resArr.find((item) => item.keyword === "cancelReason")!.disabled =
          !status.includes(res.statusCd);

        setStateTableList([...resArr]);

        setDisable(!status.includes(res.statusCd));

        setApplyId(res.retirementApplyId ?? 0);

        setState({ ...state, errorMsgId: "" });
      } else {
        setState({ ...state, errorMsgId: res.resultCd });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const handleCallApiSubmit = async (
    index: any,
    date?: any,
    reasonData?: any
  ) => {
    // 「被共済職員退職届、退職手当金請求書、合算制度利用申出書」の取り下げ更新API 呼び出し
    try {
      const res = await httpAPI_RET007({
        originalApplyId: applyId,
        originalBusinessType: "JOB-35",
        contractorNo: contractorNo,
        reason: reasonData ?? reason,
        receptionDate: date,
        screenId: "SCRR410030",
      });

      if (res.resultCd === "I0000") {
        setState({ ...state, errorMsgId: "" });

        switch (index) {
          case 0: // 合算へ切り替える
            setBusinessState({
              ...businessState,
              common: { ...businessState.common, businessType: "JOB-36" },
              job_36: {
                ...businessState.job_36,
                staffNo: location.state?.staffNo,
              },
            });

            navigate("/SCRR410004", {
              state: {
                staffNo: staffNo,
              },
            });
            break;

          case 1: // 退職金請求の取下げ
            navigate("/SCRC070008");
            break;
        }
      } else {
        setState({ ...state, errorMsgId: res.resultCd });
      }
    } catch (error) {
      navigate("error");
    }
  };

  const handleSubmit = (data: any, index: any) => {
    switch (index) {
      case 0: // 合算へ切り替える
        handleCallApiSubmit(index, null, data.cancelReason);
        break;
      case 1: // 退職金請求の取下げ
        setReason(data.cancelReason);
        isUserTypeKiko ? setOpenDate(true) : setOpenSuccess(true);
        break;
    }
  };

  return (
    <Box>
      <TkFakeTable
        list={stateTableList}
        ruleSchema={schema}
        submitButtonGroup={[
          {
            submitButtonText: "合算へ切り替える",
            submitButtonDisabled: disable,
          },
          {
            submitButtonText: "退職金請求の取下げ",
            submitButtonDisabled: disable,
          },
        ]}
        onCheckAndSubmit={(e: any, i: any) => handleSubmit(e, i)}
      />

      <TkMessageDialog
        open={openSuccess}
        title="確認（退職金請求を取下げます）"
        contextText="　退職手当金請求を取り下げますが、問題ないでしょうか。"
        okText="はい"
        cancelText="いいえ"
        onOky={() => handleCallApiSubmit(1, null)}
        onCancel={() => setOpenSuccess(false)}
      />

      <TkDateDialog
        open={openDate}
        title="確認（退職金請求を取下げます）"
        contextText="　退職手当金請求を取り下げますが、問題ないでしょうか。"
        okText="はい"
        cancelText="いいえ"
        minDate={new Date("1900/01/01")}
        maxDate={new Date()}
        onOky={(date: any) => handleCallApiSubmit(1, date)}
        onCancel={() => setOpenDate(false)}
      />
    </Box>
  );
};

export default SCRR410030;
